import { platformAssistantActionPolicySchema } from '@crest/platform-contracts/hosts';
import { z } from 'zod';
export const assistantActionProviderHostCapability = 'assistant.action-provider-host.v1';
export const assistantProposalWorkflowCapability = 'assistant.proposal-workflow.v1';
const uuid = z.string().uuid();
const identifier = z.string().min(3).max(160).regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+$/);
const versionedIdentifier = z.string().min(5).max(160)
    .regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/);
const hash = z.string().regex(/^[a-f0-9]{64}$/);
export const assistantProposalStatusSchema = z.enum([
    'pending', 'approved', 'rejected', 'expired', 'cancelled',
]);
export const assistantProviderDescriptorSchema = z.strictObject({
    providerId: identifier,
    providerModuleId: z.string().min(2).max(64).regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/),
    capability: versionedIdentifier,
    contractVersion: versionedIdentifier,
    displayName: z.string().trim().min(1).max(160),
    requiredPermission: identifier,
    actions: z.array(z.strictObject({
        actionId: versionedIdentifier,
        displayName: z.string().trim().min(1).max(160),
        targetResourceType: identifier,
        inputSchemaHash: hash,
        approvalRequired: z.literal(true),
        /**
         * REQUIRED here: every action in the Assistant registry is
         * Assistant-eligible, and Assistant rejects any action whose owner did
         * not declare versioned safety metadata. Generic Platform action-host
         * consumers keep the field optional on their own descriptor schema.
         */
        assistantPolicy: platformAssistantActionPolicySchema,
    }).superRefine((action, context) => {
        if (action.assistantPolicy.inputSchemaHash !== action.inputSchemaHash) {
            context.addIssue({
                code: 'custom', path: ['assistantPolicy', 'inputSchemaHash'],
                message: 'The Assistant policy must bind to the exact action input schema hash.',
            });
        }
    })).min(1).max(100),
});
export const assistantProposalRequestSchema = z.strictObject({
    commandId: uuid,
    providerId: identifier,
    providerModuleId: z.string().min(2).max(64).regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/),
    providerCapability: versionedIdentifier,
    actionId: versionedIdentifier,
    targetResourceType: identifier,
    targetResourceId: uuid.nullable(),
    input: z.record(z.string().min(1).max(120), z.unknown()).default({}),
    summary: z.string().trim().min(10).max(500),
    reason: z.string().trim().min(10).max(1_000),
    expiresAt: z.string().datetime({ offset: true }),
});
export const assistantProposalDecisionSchema = z.strictObject({
    commandId: uuid,
    expectedRevision: z.number().int().positive(),
    decision: z.enum(['approve', 'reject']),
    reason: z.string().trim().min(10).max(1_000),
});
export const assistantProposalSchema = z.strictObject({
    id: uuid,
    tenantId: uuid,
    providerId: identifier,
    providerModuleId: z.string().min(2).max(64).regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/),
    providerCapability: versionedIdentifier,
    actionId: versionedIdentifier,
    targetResourceType: identifier,
    targetResourceId: uuid.nullable(),
    input: z.record(z.string().min(1).max(120), z.unknown()),
    inputHash: hash,
    inputSchemaHash: hash,
    summary: z.string().min(10).max(500),
    reason: z.string().min(10).max(1_000),
    status: assistantProposalStatusSchema,
    revision: z.number().int().positive(),
    requestedByMembershipId: uuid,
    decidedByMembershipId: uuid.nullable(),
    decisionReason: z.string().min(10).max(1_000).nullable(),
    expiresAt: z.string().datetime({ offset: true }),
    createdAt: z.string().datetime({ offset: true }),
    decidedAt: z.string().datetime({ offset: true }).nullable(),
});
export const assistantProposalResponseSchema = z.strictObject({
    data: assistantProposalSchema,
    command: z.strictObject({ commandId: uuid, idempotent: z.boolean() }).optional(),
});
export const assistantProposalListQuerySchema = z.strictObject({
    status: assistantProposalStatusSchema.optional(),
    limit: z.number().int().min(1).max(100).default(50),
    offset: z.number().int().min(0).max(10_000).default(0),
});
export const assistantProposalListResponseSchema = z.strictObject({
    data: z.array(assistantProposalSchema),
    page: z.strictObject({
        limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(), total: z.number().int().nonnegative(),
    }),
});
export function defineAssistantProviderDescriptor(input) {
    return assistantProviderDescriptorSchema.parse(input);
}
//# sourceMappingURL=assistant-proposal.contract.js.map